import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { RSVP_URL, TOLCON_LOGO } from "@/lib/eventData";

const LINKS = [
  { label: "Overview", href: "#overview" },
  { label: "Program", href: "#program" },
  { label: "Menu", href: "#menu" },
  { label: "Gallery", href: "#gallery" },
  { label: "RSVP", href: "#rsvp" },
  { label: "Documents", href: "#documents" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0B2820]/90 border-b border-[#D4AF37]/25"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid="nav-brand"
          onClick={() => scrollTo("#overview")}
          className="flex items-center gap-3 group"
        >
          <span className="relative w-9 h-9 sm:w-10 sm:h-10 grid place-items-center shrink-0">
            <img
              src={TOLCON_LOGO}
              alt="Tolcon"
              className="w-full h-full object-contain"
            />
          </span>
          <span className="font-display text-[#F3E08A] text-[10px] sm:text-sm tracking-[0.22em] sm:tracking-[0.3em] whitespace-nowrap">
            <span className="hidden sm:inline">TOLCON HAVANA NIGHTS</span>
            <span className="sm:hidden">TOLCON · HAVANA</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="font-display text-[11px] tracking-[0.28em] text-[#E0D5B8] hover:text-[#F3E08A] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            data-testid="nav-cta-rsvp"
            href={RSVP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-5 py-2.5 text-[11px] rounded-sm"
          >
            RSVP Now
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#F3E08A] p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden fixed inset-x-0 top-16 sm:top-20 bottom-0 z-40 overflow-y-auto"
          style={{ backgroundColor: "#0B2820" }}
        >
          <div className="px-6 py-8 flex flex-col gap-5 min-h-full">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                onClick={() => scrollTo(l.href)}
                className="text-left font-display text-sm tracking-[0.35em] text-[#E0D5B8] hover:text-[#F3E08A] py-2 border-b border-[#D4AF37]/15"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="nav-mobile-cta-rsvp"
              href={RSVP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="btn-gold px-5 py-3.5 text-[11px] rounded-sm mt-4 text-center"
            >
              RSVP Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
